import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsent } from '../context/ConsentContext';

export default function CookieBanner() {
  const { t } = useTranslation();
  const {
    consent,
    showBanner,
    showPreferences,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
  } = useConsent();

  const [localPrefs, setLocalPrefs] = useState({
    analytics: consent.analytics,
    marketing: consent.marketing,
    preferences: consent.preferences,
  });

  // Update local prefs when consent changes
  useEffect(() => {
    setLocalPrefs({
      analytics: consent.analytics,
      marketing: consent.marketing,
      preferences: consent.preferences,
    });
  }, [consent]);

  // Handle escape key to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && showPreferences) {
      closePreferences();
    }
  }, [showPreferences, closePreferences]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!showBanner && !showPreferences) {
    return null;
  }

  const cookieTypes = [
    {
      id: 'necessary',
      name: t('cookies.necessary', 'Strictly Necessary'),
      description: t('cookies.necessaryDesc', 'These cookies are essential for the website to function properly. They enable basic functions like page navigation, secure areas access, and remembering your login. The website cannot function properly without these cookies.'),
      required: true,
    },
    {
      id: 'analytics',
      name: t('cookies.analytics', 'Analytics & Performance'),
      description: t('cookies.analyticsDesc', 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our website and your experience.'),
      required: false,
    },
    {
      id: 'marketing',
      name: t('cookies.marketing', 'Marketing & Advertising'),
      description: t('cookies.marketingDesc', 'These cookies are used to track visitors across websites and display ads that are relevant and engaging. They also help measure the effectiveness of advertising campaigns.'),
      required: false,
    },
    {
      id: 'preferences',
      name: t('cookies.preferences', 'Functionality & Preferences'),
      description: t('cookies.preferencesDesc', 'These cookies allow the website to remember choices you make (such as your language preference or region) and provide enhanced, more personalized features.'),
      required: false,
    },
  ];

  return (
    <>
      {/* Main Banner */}
      {showBanner && !showPreferences && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('cookies.title', 'We value your privacy')}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {t('cookies.description', 'We use cookies to enhance your browsing experience, serve personalized ads or content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or reject non-essential cookies.')}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6">
                  <button
                    onClick={openPreferences}
                    className="order-3 sm:order-1 px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {t('cookies.customize', 'Customize')}
                  </button>
                  <div className="flex-1 sm:flex-initial"></div>
                  <button
                    onClick={rejectAll}
                    className="order-2 px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    {t('cookies.rejectAll', 'Reject All')}
                  </button>
                  <button
                    onClick={acceptAll}
                    className="order-1 sm:order-3 px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors"
                  >
                    {t('cookies.acceptAll', 'Accept All')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-preferences-title"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closePreferences}
            aria-hidden="true"
          />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h2 id="cookie-preferences-title" className="text-xl font-semibold text-gray-900">{t('cookies.preferencesTitle', 'Cookie Preferences')}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('cookies.preferencesSubtitle', 'Manage your cookie settings below')}
                </p>
              </div>
              <button
                onClick={closePreferences}
                className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close cookie preferences"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {cookieTypes.map((type) => (
                  <div
                    key={type.id}
                    className="border border-gray-200 rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-gray-900">{type.name}</h3>
                          {type.required && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                              {t('cookies.required', 'Required')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 leading-relaxed">
                          {type.description}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={type.required || localPrefs[type.id]}
                          disabled={type.required}
                          onChange={(e) => {
                            if (!type.required) {
                              setLocalPrefs(prev => ({
                                ...prev,
                                [type.id]: e.target.checked
                              }));
                            }
                          }}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 rounded-full peer transition-colors ${
                          type.required
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-gray-200 peer-checked:bg-brand-600 peer-focus:ring-4 peer-focus:ring-brand-100'
                        }`}>
                          <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                            (type.required || localPrefs[type.id]) ? 'translate-x-5' : ''
                          }`} />
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              {/* GDPR Info */}
              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-blue-900 text-sm">{t('cookies.privacyRights', 'Your Privacy Rights')}</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      {t('cookies.privacyRightsDesc', 'Under GDPR, you have the right to access, rectify, or delete your personal data at any time. We use Google Consent Mode v2 to ensure your preferences are respected across all our tracking tools.')}{' '}
                      <a href="/privacy-policy" className="underline hover:text-blue-900 font-medium">
                        {t('cookies.readPrivacyPolicy', 'Read our Privacy Policy')}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-4 p-6 border-t border-gray-100 bg-gray-50">
              <button
                onClick={rejectAll}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {t('cookies.rejectAll', 'Reject All')}
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => savePreferences(localPrefs)}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  {t('cookies.savePreferences', 'Save Preferences')}
                </button>
                <button
                  onClick={acceptAll}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors"
                >
                  {t('cookies.acceptAll', 'Accept All')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Small floating button to reopen preferences (shown after consent is given)
export function CookieSettingsButton() {
  const { t } = useTranslation();
  const { openPreferences, isInitialized, showBanner } = useConsent();

  // Don't show if banner is visible or not initialized
  if (!isInitialized || showBanner) {
    return null;
  }

  return (
    <button
      onClick={openPreferences}
      className="fixed bottom-4 left-4 z-40 w-10 h-10 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:shadow-xl hover:scale-105 transition-all group"
      title={t('cookies.settings', 'Cookie Settings')}
      aria-label={t('cookies.openSettings', 'Open cookie settings')}
    >
      <svg className="w-5 h-5 text-gray-600 group-hover:text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    </button>
  );
}
